/**
 * Permissions — miroir frontend des contrôles serveur (Worker requireTenant /
 * requireAdmin). La session provient du Worker (GET /auth/session).
 */
import type { UserRole } from '../../types/domain';

export interface SessionUser {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  tenantId?: string;
  tenantSlug?: string;
}

/** Rôles autorisés à accéder à un espace donné. */
const SPACE_ROLES: Record<'pro' | 'client' | 'admin', UserRole[]> = {
  pro: ['professional_owner', 'professional_agent'],
  client: ['client'],
  admin: ['admin', 'moderator'],
};

export function canAccessSpace(role: UserRole, space: 'pro' | 'client' | 'admin'): boolean {
  return SPACE_ROLES[space].includes(role);
}

/** Espace d'accueil du rôle après connexion / inscription.
 *
 * Un pro qui n'a pas terminé l'onboarding arrive sur /pro/onboarding,
 * pas sur /pro/dashboard.
 */
export function homeForRole(role: UserRole, onboardingComplete?: boolean): string {
  if (canAccessSpace(role, 'admin')) return '/admin';
  if (canAccessSpace(role, 'pro')) {
    if (onboardingComplete === false) return '/pro/onboarding';
    return '/pro/dashboard';
  }
  return '/app';
}

/** Le rôle a-t-il un espace client ? */
export function isClient(role: UserRole): boolean {
  return canAccessSpace(role, 'client');
}

/** Le rôle a-t-il un espace pro ? */
export function isPro(role: UserRole): boolean {
  return canAccessSpace(role, 'pro');
}

/** Le rôle a-t-il un espace admin ? */
export function isAdmin(role: UserRole): boolean {
  return canAccessSpace(role, 'admin');
}

/** Vérifie si un pro a terminé l'onboarding dans le frontend.
 * À utiliser quand le state Redux onboarding est disponible.
 * Retourne false si l'information n'est pas encore connue.
 */
export function isOnboardingComplete(role: UserRole, tenantId?: string): boolean {
  if (!canAccessSpace(role, 'pro') || !tenantId) return false;
  // Sur le frontend, cette information vient du slice onboarding.
  // Ici on ne peut pas savoir sans accès au store ; on considère non terminé.
  return false;
}

/** Helper pour l'inscription : tous les nouveaux comptes pros doivent
 * démarrer l'onboarding, même s'ils ont fourni métier/ville.
 */
export function isNewProAccount(role: UserRole, tenantId?: string): boolean {
  return canAccessSpace(role, 'pro') && Boolean(tenantId);
}
